import type { EntityAnnotationBatch, EntityAnnotationEntity } from '../../entity-annotations';

import entityAnnotationMarkers from './markers';

const findUniqueOffset = (content: string, query: string): number | undefined => {
	const firstOffset = content.indexOf(query);
	if (firstOffset === -1 || content.indexOf(query, firstOffset + query.length) !== -1) {
		return undefined;
	}

	return firstOffset;
};

type SourceRange = {
	start: number;
	end: number;
	strategy: 'sentence' | 'sentence-prefix' | 'normalized-sentence' | 'unique-mention';
};

type WhitespaceNormalizedText = {
	originalOffsets: number[];
	text: string;
};

const normalizeWhitespace = (value: string): WhitespaceNormalizedText => {
	const originalOffsets: number[] = [];
	let text = '';
	let isInWhitespace = false;

	for (let index = 0; index < value.length; index++) {
		const character = value[index];
		if (/\s/.test(character)) {
			if (!isInWhitespace) {
				text += ' ';
				originalOffsets.push(index);
				isInWhitespace = true;
			}
			continue;
		}

		text += character;
		originalOffsets.push(index);
		isInWhitespace = false;
	}

	return { originalOffsets, text };
};

const getNormalizedIndex = (originalOffsets: readonly number[], sourceOffset: number): number => {
	const index = originalOffsets.findIndex((offset) => offset >= sourceOffset);
	return index === -1 ? originalOffsets.length : index;
};

const getSourceRange = ({
	content,
	entity,
	sentence,
}: {
	content: string;
	entity: EntityAnnotationEntity;
	sentence: string;
}): SourceRange | undefined => {
	if (entity.start < 0 || entity.end <= entity.start || entity.end > sentence.length) {
		return undefined;
	}

	const sentenceOffset = findUniqueOffset(content, sentence);
	if (sentenceOffset !== undefined) {
		const start = sentenceOffset + entity.start;
		const end = sentenceOffset + entity.end;
		if (content.slice(start, end) === entity.mention) {
			return { start, end, strategy: 'sentence' };
		}
	}

	const sentencePrefix = sentence.slice(0, entity.end);
	const prefixOffset = findUniqueOffset(content, sentencePrefix);
	if (prefixOffset !== undefined) {
		const start = prefixOffset + entity.start;
		const end = prefixOffset + entity.end;
		if (content.slice(start, end) === entity.mention) {
			return { start, end, strategy: 'sentence-prefix' };
		}
	}

	const normalizedContent = normalizeWhitespace(content);
	const normalizedSentence = normalizeWhitespace(sentence);
	const normalizedSentenceOffset = findUniqueOffset(
		normalizedContent.text,
		normalizedSentence.text,
	);
	if (normalizedSentenceOffset !== undefined) {
		const normalizedStart =
			normalizedSentenceOffset +
			getNormalizedIndex(normalizedSentence.originalOffsets, entity.start);
		const normalizedEnd =
			normalizedSentenceOffset + getNormalizedIndex(normalizedSentence.originalOffsets, entity.end);
		const start = normalizedContent.originalOffsets[normalizedStart];
		const end =
			normalizedEnd === normalizedContent.originalOffsets.length
				? content.length
				: normalizedContent.originalOffsets[normalizedEnd];

		if (start !== undefined && end !== undefined && content.slice(start, end) === entity.mention) {
			return { start, end, strategy: 'normalized-sentence' };
		}
	}

	const mentionOffset = findUniqueOffset(content, entity.mention);
	return mentionOffset === undefined
		? undefined
		: {
				start: mentionOffset,
				end: mentionOffset + entity.mention.length,
				strategy: 'unique-mention',
			};
};

const isInferredProject = (entity: EntityAnnotationEntity): boolean =>
	entity.type === 'InferredProject';

const createStartMarker = (entity: EntityAnnotationEntity): string =>
	`${entityAnnotationMarkers.start}start:${encodeURIComponent(JSON.stringify(entity))}${entityAnnotationMarkers.end}`;

export default function annotateEntityMarkdown(
	content: string,
	annotationBatches: readonly EntityAnnotationBatch[] | undefined,
): string {
	if (!annotationBatches?.length) {
		return content;
	}

	const ranges = annotationBatches
		.flatMap(({ entities, sentence }) =>
			entities.flatMap((entity) => {
				if (!isInferredProject(entity)) {
					return [];
				}

				const range = getSourceRange({ content, entity, sentence });
				return range ? [{ ...range, entity }] : [];
			}),
		)
		.sort((first, second) => second.start - first.start);
	let annotatedContent = content;
	let nextEnd = content.length;
	for (const range of ranges) {
		if (range.end > nextEnd) {
			continue;
		}

		annotatedContent =
			annotatedContent.slice(0, range.start) +
			createStartMarker(range.entity) +
			annotatedContent.slice(range.start, range.end) +
			entityAnnotationMarkers.endMarker +
			annotatedContent.slice(range.end);
		nextEnd = range.start;
	}

	return annotatedContent;
}
